<template>
    <div class="container" style="min-height: 85vh">
        <div class="columns">
            <div class="column is-8 is-offset-2">
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="media">
                            <div class="media-content">
                                <h1 class="title is-1">
                                    Sharing Open Data with BossDB
                                </h1>
                                <p class="subtitle is-6">
                                    Updated: October 1, 2023
                                </p>
                                <!-- tags: -->
                                <p class="subtitle is-6">
                                    <span class="tag is-primary">#Open-Data</span>&nbsp;
                                    <span class="tag is-primary">#Publication</span>&nbsp;
                                    <span class="tag is-primary">#Scientific-Communication</span>&nbsp;
                                </p>

                                <hr />
                            </div>
                        </div>
                        <div class="content">
                            <p>
                                It is increasingly important and common to share
                                datasets with the world when publishing in
                                high-impact scientific journals. Indeed, many
                                journals require that researchers deposit their
                                data in a repository that is open to the public
                                upon publication.
                            </p>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <BossDBCitationGuidance />
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-unlock"></i> </span>&nbsp;&nbsp;Account-free data access
                            </h2>
                            <p>
                                BossDB makes it easy to share your data
                                privately with collaborators, and then — with
                                the flip of a switch — to make it public. Public
                                BossDB datasets are available to anyone who has
                                a link to the dataset, and so
                                <b>BossDB meets most journals' requirements
                                    for unauthenticated data access</b>.
                            </p>
                            <br />
                            To see an example of this in action, click through
                            to visualize data from
                            <a
                                href="https://neuroglancer.bossdb.io/#!%7B%22dimensions%22:%7B%22x%22:%5B1e-9%2C%22m%22%5D%2C%22y%22:%5B1e-9%2C%22m%22%5D%2C%22z%22:%5B3.0000000000000004e-8%2C%22m%22%5D%7D%2C%22position%22:%5B12044.064453125%2C8481.0166015625%2C215.57823181152344%5D%2C%22crossSectionScale%22:27.734936697335293%2C%22projectionOrientation%22:%5B0.2756569981575012%2C0.39076173305511475%2C-0.2699752449989319%2C0.8357223272323608%5D%2C%22projectionScale%22:5353.387055832315%2C%22layers%22:%5B%7B%22type%22:%22image%22%2C%22source%22:%22precomputed://s3://bossdb-open-data/witvliet2020/Dataset_4/em%22%2C%22tab%22:%22source%22%2C%22name%22:%22Dataset_4%22%7D%2C%7B%22type%22:%22segmentation%22%2C%22source%22:%22precomputed://s3://bossdb-open-data/witvliet2020/Dataset_4/segmentation%22%2C%22tab%22:%22source%22%2C%22name%22:%22segmentation%22%7D%5D%2C%22selectedLayer%22:%7B%22layer%22:%22segmentation%22%7D%2C%22layout%22:%224panel%22%7D">
                                <i>Witvliet et al., Nature 2021</i> </a>.
                            <blockquote>
                                Witvliet, D., Mulcahy, B., Mitchell, J.K. et al.
                                <b>Connectomes across development reveal
                                    principles of brain maturation.</b>
                                Nature 596, 257-261 (2021).
                                https://doi.org/10.1038/s41586-021-03778-8
                            </blockquote>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-university"></i> </span>&nbsp;&nbsp;Stable identifiers
                            </h2>
                            <p>
                                BossDB guarantees project page and dataset links
                                (<code>https://bossdb.org/project/[ProjectName]</code>)
                                for at least <b>five years</b>, and provides a
                                <b>stable, persistent identifier</b> that you
                                can use in your publication.
                            </p>
                            <p>
                                <b>Update October 2023:</b> BossDB now also assigns
                                DOIs to all Data Collections. For more information,
                                see our announcement: <router-link to="/post/bossdb-dois">
                                    <div class="tag">Announcing BossDB dataset DOIs</div>
                                </router-link>.
                            </p>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-graduation-cap"></i> </span>&nbsp;&nbsp;Institution-backed
                            </h2>
                            <p>
                                BossDB is developed and hosted by the
                                <a href="https://jhuapl.edu/">Johns Hopkins Applied Physics Laboratory</a>, with support
                                from
                                <a
                                    href="https://aws.amazon.com/blogs/publicsector/the-boss-a-petascale-database-for-large-scale-neuroscience-powered-by-serverless-technologies/">Amazon
                                    Web Services</a>.
                            </p>
                            <p>
                                BossDB is the
                                <a
                                    href="https://braininitiative.nih.gov/funding-opportunies/notice-data-sharing-policy-brain-initiative%C2%AE">NIH
                                    data repository of record for the BRAIN
                                    Initiative</a>
                                for electron microscopy and X-ray
                                microtomography data.
                            </p>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-book"></i> </span>&nbsp;&nbsp;Prior journal publications hosted
                                on BossDB
                            </h2>
                            <p>
                                BossDB hosts data for papers published in the
                                following journals.
                            </p>
                            <div style="vertical-align: top; font-weight: bold">
                                <ul>
                                    <li>Cell</li>
                                    <li>Cell Current Biology</li>
                                    <li>eLife</li>
                                    <li>eNeuro</li>
                                    <li>Journal of Neuroscience</li>
                                    <li>Nature Methods</li>
                                    <li>Nature Neuroscience</li>
                                    <li>Nature Scientific Data</li>
                                    <li>Neuron</li>
                                    <li>SPIE</li>
                                    <br />
                                </ul>
                            </div>
                            <small>This precedent is not a guarantee of future
                                approval.</small>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-gavel"></i> </span>&nbsp;&nbsp;Licensing
                            </h2>
                            <p>
                                We are making all the material on this website
                                available under the
                                <b>
                                    <a href="https://creativecommons.org/licenses/by/4.0/">Creative Commons Attribution 4.0
                                        International Public Licence</a> </b>, which is approved by most scientific journals
                                with a public data requirement.
                            </p>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                <div class="card">
                    <div class="card-content">
                        <div class="content">
                            <h2>
                                <span class="icon">
                                    <i class="fa fa-question"></i> </span>&nbsp;&nbsp;Additional Information
                            </h2>
                            <p>
                                For additional information, please see our
                                Privacy Policy and Terms of Service:
                            </p>
                            <a href="mailto:info@bossdb.org">Privacy Policy</a>
                            <br />
                            <br />
                            <a href="mailto:info@bossdb.org">Terms of Service</a>
                            <hr />
                            <p>
                                If you have a question about hosting open data
                                with BossDB, get in touch at
                                <a href="mailto:info@bossdb.org">info@bossdb.org</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <br />
        <br />
    </div>
</template>

<script>
import BossDBCitationGuidance from "@/components/BossDBCitationGuidance.vue";

export default {
    name: "OpenData",

    components: {
        BossDBCitationGuidance,
    },
};
</script>
